import { useLocation, useNavigate } from "react-router-dom";
import { authSession } from "../store/useAuthStore";
import {
  portalForPath,
  resolvePortals,
  schoolSwitchDestination,
} from "../utils/auth-navigation";
import { useAuth } from "./useAuth";

/** School changes reuse session invalidation; portal changes only navigate among already granted workspaces. */
export function useContextSwitch(onDismiss: () => void) {
  const { user, activeTenant, roles, requestScope, switchTenant } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const portals = resolvePortals(roles, activeTenant?.adminTitle);
  const current = portalForPath(location.pathname);
  const currentPortal = portals.find((portal) => portal.role === current?.role);

  const selectSchool = (tenantId: string) => {
    if (!authSession.isCurrent(requestScope)) return;
    const tenant = user?.tenants.find((item) => item.tenantId === tenantId);
    if (!tenant) return;
    onDismiss();
    if (tenantId === activeTenant?.tenantId) return;
    const destination = schoolSwitchDestination(
      tenant.roles,
      location.pathname,
    );
    // Existing lifecycle subscribers cancel old-school requests and clear private caches synchronously.
    switchTenant(tenantId);
    void navigate(destination, { replace: true, state: null });
  };

  const selectPortal = (role: string) => {
    if (!authSession.isCurrent(requestScope)) return;
    const portal = portals.find((item) => item.role === role);
    if (!portal) return;
    onDismiss();
    if (portal.role !== currentPortal?.role)
      void navigate(portal.home, { state: null });
  };

  return {
    schools: user?.tenants ?? [],
    activeTenant,
    portals,
    currentPortal,
    selectSchool,
    selectPortal,
  };
}
